import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, SectionLabel } from "./Reveal";

const STEPS = [
  { n: "01", title: "UNDERSTAND THE BUSINESS", copy: "Volumes, service model, growth intent and unit economics come first." },
  { n: "02", title: "UNDERSTAND THE MENU", copy: "Every SKU is mapped for complexity, preparation and shared ingredients." },
  { n: "03", title: "MAP PRODUCTION", copy: "Batch, cook-to-order and mixed flows are charted across the day." },
  { n: "04", title: "MODEL MANPOWER", copy: "Staffing follows real production load — not guesswork or industry habit." },
  { n: "05", title: "DESIGN THE WORKFLOW", copy: "Movement, handoffs and the pass are designed to remove bottlenecks." },
  { n: "06", title: "PLAN THE KITCHEN", copy: "Only now do layout, stations and equipment get specified." },
  { n: "07", title: "BUILD FOR SCALE", copy: "Systems are documented so the next outlet is a replication, not a reinvention." },
];

const HowWeWork = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.7", "end 0.6"] });
  const lineScale = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section id="approach" ref={ref} className="relative bg-[#f6f6f6] py-28 lg:py-40 border-y border-ink/5" data-testid="approach-section">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-32">
            <Reveal><SectionLabel>How We Work — 07</SectionLabel></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-display font-semibold text-3xl lg:text-5xl tracking-tight text-ink leading-[1.08]" data-testid="approach-headline">
                GOOD KITCHENS DON'T START WITH EQUIPMENT.
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-ink/55 leading-relaxed max-w-md">
                They start with the business. Seven steps, in order — because each one
                determines the next.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="lg:col-span-7 relative">
          <div className="absolute left-0 top-0 bottom-0 w-px bg-ink/10" aria-hidden="true">
            <motion.div style={{ scaleY: lineScale }} className="w-full h-full bg-skope origin-top" />
          </div>
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={0.05 * i} className="relative pl-10 lg:pl-14 py-8 border-b border-ink/5 last:border-0">
              <div className="flex flex-col lg:flex-row lg:items-baseline gap-2 lg:gap-8" data-testid={`approach-step-${s.n}`}>
                <span className="font-mono text-sm text-skope">{s.n}</span>
                <div>
                  <h3 className="font-display font-semibold text-xl lg:text-3xl tracking-tight text-ink/90">{s.title}</h3>
                  <p className="mt-2 text-sm text-ink/45 leading-relaxed max-w-md">{s.copy}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowWeWork;
